import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PageHeader } from '../components/ui';
import {
  seriesFormatApi,
  postIdeaApi,
  editorialPlanApi,
  contentBriefApi,
  SeriesFormat,
  PostIdea,
  EditorialPlan,
  ContentBrief
} from '../services/api';

type WorkflowStep = 'overview' | 'post-ideas' | 'editorial-plan' | 'content-briefs' | 'final-content';

const SeriesFormatWorkflowPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [seriesFormat, setSeriesFormat] = useState<SeriesFormat | null>(null);
  const [postIdeas, setPostIdeas] = useState<PostIdea[]>([]);
  const [editorialPlans, setEditorialPlans] = useState<EditorialPlan[]>([]);
  const [contentBriefs, setContentBriefs] = useState<ContentBrief[]>([]);

  const [currentStep, setCurrentStep] = useState<WorkflowStep>('overview');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadData = useCallback(async () => {
    if (!id) return;

    try {
      setLoading(true);
      const [format, ideas] = await Promise.all([
        seriesFormatApi.getById(id),
        postIdeaApi.getAll({ seriesFormatId: id }),
      ]);

      setSeriesFormat(format);
      setPostIdeas(ideas);

      // Load editorial plans
      const plans = await editorialPlanApi.getAll();
      setEditorialPlans(plans.filter((p: EditorialPlan) =>
        p.postIdeaIds.some((ideaId: string) => ideas.some((idea: PostIdea) => idea.id === ideaId))
      ));

      // Load content briefs for each post idea
      const briefPromises = ideas.map((idea: PostIdea) =>
        contentBriefApi.getByPostIdea(idea.id).catch(() => null)
      );
      const briefs = (await Promise.all(briefPromises)).filter((b): b is ContentBrief => b !== null);
      setContentBriefs(briefs);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const getStepStatus = (step: WorkflowStep): 'completed' | 'current' | 'pending' => {
    const steps: WorkflowStep[] = ['overview', 'post-ideas', 'editorial-plan', 'content-briefs', 'final-content'];
    const currentIndex = steps.indexOf(currentStep);
    const stepIndex = steps.indexOf(step);

    if (step === 'overview') return 'completed';
    if (step === 'post-ideas') return postIdeas.length >= 20 ? 'completed' : (stepIndex <= currentIndex ? 'current' : 'pending');
    if (step === 'editorial-plan') return editorialPlans.length > 0 ? 'completed' : (stepIndex <= currentIndex ? 'current' : 'pending');
    if (step === 'content-briefs') return contentBriefs.length > 0 ? 'completed' : (stepIndex <= currentIndex ? 'current' : 'pending');
    if (step === 'final-content') return stepIndex <= currentIndex ? 'current' : 'pending';

    return 'pending';
  };

  const handleExecutePrompt5 = () => {
    // Navigate to workflow execution for Prompt 5
    navigate(`/workflows/execute-template?template=prompt-5&seriesFormatId=${id}`);
  };

  const handleExecutePrompt6 = () => {
    navigate(`/workflows/execute-template?template=prompt-6&seriesFormatId=${id}`);
  };

  const handleExecutePrompt7 = (postIdeaId: string) => {
    navigate(`/workflows/execute-template?template=prompt-7&postIdeaId=${postIdeaId}`);
  };

  const handleExecutePrompt8 = (briefId: string) => {
    navigate(`/workflows/execute-template?template=prompt-8&briefId=${briefId}`);
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <span className="loading-text">Laden...</span>
      </div>
    );
  }

  if (!seriesFormat) {
    return (
      <div className="page-container">
        <div className="alert alert-warning">Serienformat nicht gefunden</div>
      </div>
    );
  }

  const renderProgressBar = () => {
    const steps = [
      { id: 'overview', label: 'Übersicht', icon: '📋' },
      { id: 'post-ideas', label: 'Beitragsideen', icon: '💡' },
      { id: 'editorial-plan', label: 'Wochenplan (Slots)', icon: '📅' },
      { id: 'content-briefs', label: 'Kurzbeschreibungen', icon: '📝' },
      { id: 'final-content', label: 'Content Erstellung', icon: '✨' }
    ];

    return (
      <div className="workflow-progress">
        {steps.map((step, index) => {
          const status = getStepStatus(step.id as WorkflowStep);
          return (
            <div key={step.id} className="workflow-progress-step">
              {index > 0 && (
                <div className={`workflow-progress-line ${
                  status === 'completed' ? 'workflow-progress-line-completed' : 'workflow-progress-line-pending'
                }`} />
              )}
              <div
                onClick={() => setCurrentStep(step.id as WorkflowStep)}
                className={`workflow-step-circle ${
                  status === 'completed' ? 'workflow-step-circle-completed' :
                  status === 'current' ? 'workflow-step-circle-current' :
                  'workflow-step-circle-pending'
                }`}
              >
                {status === 'completed' ? '✓' : step.icon}
              </div>
              <span className={`workflow-step-label ${
                status === 'current' ? 'workflow-step-label-active' : ''
              }`}>
                {step.label}
              </span>
            </div>
          );
        })}
      </div>
    );
  };

  const renderOverview = () => (
    <div>
      <h2>Übersicht: {seriesFormat.name}</h2>
      <div className="card mb-6">
        <h3 className="card-title">{seriesFormat.title}</h3>
        {seriesFormat.description && <p className="card-description">{seriesFormat.description}</p>}
        {seriesFormat.schedule && (
          <p className="mt-2"><strong>📅 Zeitplan:</strong> {seriesFormat.schedule}</p>
        )}
        {seriesFormat.footer && (
          <div className="mt-4">
            <strong>Fußzeile:</strong>
            <pre className="code-block mt-2">
              {seriesFormat.footer}
            </pre>
          </div>
        )}
      </div>

      <div className="grid-cards-3">
        <div className="card">
          <h3 className="card-title">💡 Beitragsideen</h3>
          <p className="card-stat-number">
            {postIdeas.length}
          </p>
          <p className="text-sm text-secondary">
            {postIdeas.length >= 20 ? '✅ Ziel erreicht (20+)' : `⏳ Noch ${20 - postIdeas.length} bis Ziel`}
          </p>
        </div>

        <div className="card">
          <h3 className="card-title">📅 Redaktionspläne</h3>
          <p className="card-stat-number">
            {editorialPlans.length}
          </p>
          <p className="text-sm text-secondary">
            {editorialPlans.length > 0 ? '✅ Erstellt' : '⏳ Noch nicht erstellt'}
          </p>
        </div>

        <div className="card">
          <h3 className="card-title">📝 Kurzbeschreibungen</h3>
          <p className="card-stat-number">
            {contentBriefs.length}
          </p>
          <p className="text-sm text-secondary">
            {contentBriefs.length > 0 ? `✅ ${contentBriefs.length} erstellt` : '⏳ Noch keine erstellt'}
          </p>
        </div>
      </div>

      <div className="mt-6">
        <h3>🚀 Nächste Schritte</h3>
        <button
          onClick={() => setCurrentStep('post-ideas')}
          className="btn btn-primary btn-lg btn-full mt-2"
        >
          {postIdeas.length === 0 ? 'Starte mit Schritt 1: Beitragsideen generieren' : 'Weiter zum nächsten Schritt'}
        </button>
      </div>
    </div>
  );

  const renderPostIdeas = () => (
    <div>
      <h2>💡 Schritt 1: Beitragsideen generieren (Prompt 5)</h2>

      <div className="info-panel info-panel-primary">
        <h4 className="info-panel-title">📖 Was passiert in diesem Schritt?</h4>
        <p>Der AI-Workflow analysiert die Pain Points deiner Zielgruppe und generiert 20 konkrete Beitragsideen für "{seriesFormat.name}".</p>
        <ul>
          <li>Analyse der Perplexity-Recherche</li>
          <li>Eigenständige Marktanalyse</li>
          <li>Abgleich und Zusammenführung</li>
          <li>20 konkrete Beitragsideen mit Arbeitstiteln</li>
        </ul>
      </div>

      {postIdeas.length === 0 ? (
        <div className="empty-state">
          <p className="empty-state-title">
            Noch keine Beitragsideen für "{seriesFormat.name}" erstellt.
          </p>
          <button
            onClick={handleExecutePrompt5}
            className="btn btn-success btn-lg"
          >
            🚀 Prompt 5 Workflow starten
          </button>
        </div>
      ) : (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h3>{postIdeas.length} Beitragsideen</h3>
            <button
              onClick={handleExecutePrompt5}
              className="btn btn-primary"
            >
              + Weitere Ideen generieren
            </button>
          </div>

          <div className="grid-cards-3">
            {postIdeas.map((idea, index) => (
              <div key={idea.id} className="card">
                <div className="flex justify-between">
                  <span className="badge badge-primary">
                    #{index + 1}
                  </span>
                  <span className={`badge ${
                    idea.status === 'posted' ? 'badge-success' :
                    idea.status === 'scheduled' ? 'badge-info' :
                    idea.status === 'created' ? 'badge-warning' :
                    'badge-neutral'
                  }`}>
                    {idea.status}
                  </span>
                </div>
                <h4 className="mt-2 mb-2">{idea.title}</h4>
                <p className="text-sm text-secondary">
                  <strong>Thema:</strong> {idea.topic}
                </p>
                {idea.painPoint && (
                  <p className="text-xs text-muted mt-1" style={{ fontStyle: 'italic' }}>
                    💡 {idea.painPoint}
                  </p>
                )}
              </div>
            ))}
          </div>

          <div className="mt-6 text-center">
            <button
              onClick={() => setCurrentStep('editorial-plan')}
              disabled={postIdeas.length < 12}
              className={`btn btn-lg ${postIdeas.length >= 12 ? 'btn-success' : 'btn-secondary'}`}
            >
              Weiter zu Schritt 2: Wochenplan erstellen
            </button>
            {postIdeas.length < 12 && (
              <p className="text-sm mt-2" style={{ color: 'var(--color-error)' }}>
                Mindestens 12 Beitragsideen benötigt für einen 4-Wochen-Plan
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );

  const renderEditorialPlan = () => (
    <div>
      <h2>📅 Schritt 2: Wochenplan erstellen (Prompt 6)</h2>

      <div className="info-panel info-panel-primary">
        <h4 className="info-panel-title">📖 Was passiert in diesem Schritt?</h4>
        <p>Der AI-Workflow erstellt einen strukturierten Wochenplan über 4 Wochen mit 3 Posts pro Woche (Dienstag, Donnerstag, Freitag).</p>
      </div>

      {postIdeas.length < 12 ? (
        <div className="info-panel info-panel-warning text-center">
          <p className="info-panel-title">
            ⚠️ Du benötigst mindestens 12 Beitragsideen um einen 4-Wochen-Plan zu erstellen.
          </p>
          <p>Aktuell: {postIdeas.length} Ideen | Benötigt: 12 Ideen</p>
          <button
            onClick={() => setCurrentStep('post-ideas')}
            className="btn btn-primary mt-4"
          >
            Zurück zu Beitragsideen
          </button>
        </div>
      ) : editorialPlans.length === 0 ? (
        <div className="empty-state">
          <p className="empty-state-title">
            Du hast {postIdeas.length} Beitragsideen. Bereit für die Planung!
          </p>
          <button
            onClick={handleExecutePrompt6}
            className="btn btn-success btn-lg"
          >
            🚀 Prompt 6 Workflow starten
          </button>
        </div>
      ) : (
        <div>
          <h3>{editorialPlans.length} {editorialPlans.length === 1 ? 'Wochenplan' : 'Wochenpläne'} erstellt</h3>

          {editorialPlans.map((plan) => (
            <div key={plan.id} className="card mb-4">
              <h4 className="card-title">{plan.name}</h4>
              <p className="text-sm mt-2">
                <strong>Zeitraum:</strong> {new Date(plan.startDate).toLocaleDateString('de-DE')} - {new Date(plan.endDate).toLocaleDateString('de-DE')}
              </p>
              <p className="text-sm mt-1">
                <strong>Posts:</strong> {plan.postIdeaIds.length} Beiträge geplant ({plan.postsPerWeek}x pro Woche)
              </p>
              <button
                onClick={() => navigate('/editorial-plan')}
                className="btn btn-primary mt-4"
              >
                📅 Plan anzeigen
              </button>
            </div>
          ))}

          <div className="mt-6 text-center">
            <button
              onClick={() => setCurrentStep('content-briefs')}
              className="btn btn-success btn-lg"
            >
              Weiter zu Schritt 3: Kurzbeschreibungen erstellen
            </button>
          </div>
        </div>
      )}
    </div>
  );

  const renderContentBriefs = () => (
    <div>
      <h2>📝 Schritt 3: Kurzbeschreibungen erstellen (Prompt 7)</h2>

      <div className="info-panel info-panel-primary">
        <h4 className="info-panel-title">📖 Was passiert in diesem Schritt?</h4>
        <p>Für jede Beitragsidee wird eine strukturierte Kurzbeschreibung erstellt mit: Thema, Herausforderung, Wendepunkt und Learning.</p>
      </div>

      <div className="mb-6">
        <h3>Beitragsideen ohne Kurzbeschreibung</h3>
        <div className="grid-cards-3">
          {postIdeas
            .filter(idea => !contentBriefs.some(b => b.postIdeaId === idea.id))
            .map((idea) => (
              <div key={idea.id} className="card">
                <h4 className="card-title">{idea.title}</h4>
                <p className="text-sm text-secondary mt-2">
                  {idea.topic}
                </p>
                <button
                  onClick={() => handleExecutePrompt7(idea.id)}
                  className="btn btn-success btn-full mt-4"
                >
                  🚀 Brief erstellen
                </button>
              </div>
            ))}
        </div>
      </div>

      {contentBriefs.length > 0 && (
        <div className="mt-6">
          <h3>{contentBriefs.length} Kurzbeschreibung(en) erstellt</h3>
          <div className="grid-cards-3">
            {contentBriefs.map((brief) => {
              const idea = postIdeas.find(i => i.id === brief.postIdeaId);
              return (
                <div key={brief.id} className="card card-completed-border">
                  <h4 className="card-title">✅ {idea?.title || 'Unbekannt'}</h4>
                  <p className="text-sm mt-2">
                    <strong>Thema:</strong> {brief.theme}
                  </p>
                  <p className="text-sm mt-1">
                    <strong>Learning:</strong> {brief.learning}
                  </p>
                  <button
                    onClick={() => navigate(`/content-briefs?postIdeaId=${brief.postIdeaId}`)}
                    className="btn btn-primary btn-full mt-4"
                  >
                    📄 Details anzeigen
                  </button>
                </div>
              );
            })}
          </div>

          <div className="mt-6 text-center">
            <button
              onClick={() => setCurrentStep('final-content')}
              disabled={contentBriefs.length === 0}
              className={`btn btn-lg ${contentBriefs.length > 0 ? 'btn-success' : 'btn-secondary'}`}
            >
              Weiter zu Schritt 4: Content Erstellung mit Claude
            </button>
          </div>
        </div>
      )}
    </div>
  );

  const renderFinalContent = () => (
    <div>
      <h2>✨ Schritt 4: Finale Content-Erstellung (Prompt 8)</h2>

      <div className="info-panel info-panel-primary">
        <h4 className="info-panel-title">📖 Was passiert in diesem Schritt?</h4>
        <p>Claude wird mit deinem Master-Prompt "programmiert" und erstellt dann fertige LinkedIn-Posts basierend auf den Kurzbeschreibungen.</p>
        <ul>
          <li>Setup von Claude mit deinem persönlichen Profil</li>
          <li>Automatische Integration von Serienformat-Footern</li>
          <li>Generierung von fertigen, publikationsreifen Posts</li>
        </ul>
      </div>

      {contentBriefs.length === 0 ? (
        <div className="info-panel info-panel-warning text-center">
          <p className="info-panel-title">
            ⚠️ Du benötigst mindestens eine Kurzbeschreibung um Content zu erstellen.
          </p>
          <button
            onClick={() => setCurrentStep('content-briefs')}
            className="btn btn-primary mt-4"
          >
            Zurück zu Kurzbeschreibungen
          </button>
        </div>
      ) : (
        <div>
          <h3>Kurzbeschreibungen bereit für Content-Erstellung</h3>
          <div className="grid-cards-3">
            {contentBriefs.map((brief) => {
              const idea = postIdeas.find(i => i.id === brief.postIdeaId);
              return (
                <div key={brief.id} className="card">
                  <h4 className="card-title">{idea?.title || 'Unbekannt'}</h4>
                  <p className="text-xs text-secondary mt-2">
                    <strong>Thema:</strong> {brief.theme}
                  </p>
                  <p className="text-xs text-secondary mt-1">
                    <strong>Learning:</strong> {brief.learning}
                  </p>
                  <button
                    onClick={() => handleExecutePrompt8(brief.id)}
                    className="btn btn-primary btn-full mt-4"
                  >
                    ✨ Mit Claude erstellen
                  </button>
                </div>
              );
            })}
          </div>

          <div className="info-panel info-panel-success mt-6 text-center">
            <h3 className="info-panel-title">🎉 Workflow abgeschlossen!</h3>
            <p>Du hast alle Schritte für das Serienformat "{seriesFormat.name}" durchlaufen.</p>
            <div className="btn-group justify-center mt-4">
              <button
                onClick={() => navigate('/series-formats')}
                className="btn btn-primary"
              >
                📋 Zurück zur Übersicht
              </button>
              <button
                onClick={() => setCurrentStep('overview')}
                className="btn btn-secondary"
              >
                🔄 Workflow-Übersicht
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="page-container">
      <div className="mb-4">
        <button
          onClick={() => navigate('/series-formats')}
          className="btn btn-secondary btn-sm"
        >
          ← Zurück zu Serienformate
        </button>
      </div>

      <PageHeader
        title={`Workflow: ${seriesFormat.name}`}
        subtitle="Führt dieses Serienformat Schritt für Schritt von den Beitragsideen über Wochenplan und Beitragsentwürfe (Content Briefs) zum fertigen Beitrag."
      />

      {error && (
        <div className="alert alert-error">
          {error}
        </div>
      )}

      {renderProgressBar()}

      <div className="workflow-content">
        {currentStep === 'overview' && renderOverview()}
        {currentStep === 'post-ideas' && renderPostIdeas()}
        {currentStep === 'editorial-plan' && renderEditorialPlan()}
        {currentStep === 'content-briefs' && renderContentBriefs()}
        {currentStep === 'final-content' && renderFinalContent()}
      </div>
    </div>
  );
};

export default SeriesFormatWorkflowPage;
